import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { getShop, buySkin, createCheckout, getMe, buyKit, getInventory } from '../api/api';

/* Reusable Fragz coin icon */
const FragzIcon = ({ size = 20 }) => (
  <img src="/assets/fragz.png" alt="Fragz" style={{ width: size, height: size, objectFit: 'contain', verticalAlign: 'middle' }} />
);

const C = {
  red: '#E8192C',
  redDim: '#a4111d',
  redGlow: 'rgba(232,25,44,0.45)',
  orange: '#FF6B2B',
  bg: '#0C0905',
  bgWarm: '#100B07',
  bgWarmer: '#15100B',
  text: '#F0F2F5',
  textDim: '#9A938B',
  textVeryDim: '#5C554E',
  card: '#111111',
  cardAlt: '#16110D',
  border: '#1e1e1e',
  borderBright: '#2A2520',
  green: '#3FCB7E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body: '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span aria-hidden="true" style={{
    display: 'inline-block',
    width: size,
    height: size,
    border: `${Math.max(1.5, size / 9)}px solid ${color}`,
    background: hollow ? 'transparent' : color,
    transform: 'rotate(45deg)',
    position: 'relative',
    boxSizing: 'border-box',
    flexShrink: 0,
    ...style,
  }}>
    {withDot && (
      <span style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: size * 0.32,
        height: size * 0.32,
        background: dotColor,
        transform: 'translate(-50%, -50%)',
      }} />
    )}
  </span>
);

const Eyebrow = ({ children, color = C.red, style }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: F.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 3,
    textTransform: 'uppercase',
    color: C.textDim,
    ...style,
  }}>
    <Diamond size={7} color={color} hollow={false} />
    <span>{children}</span>
  </div>
);

const SectionHeader = ({ kicker, title, children }) => (
  <div style={st.sectionHead}>
    <div>
      <Eyebrow>{kicker}</Eyebrow>
      <h2 style={st.sectionTitle}>{title}</h2>
    </div>
    {children && <p style={st.sectionCopy}>{children}</p>}
  </div>
);

/* ═══════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════ */

const RARITY_COLORS = {
  'Consumer Grade': '#b0c3d9', 'Industrial Grade': '#5e98d9',
  'Mil-Spec': '#4b69ff', 'Restricted': '#8847ff',
  'Classified': '#d32ce6', 'Covert': '#eb4b4b',
  'Extraordinary': '#ffd700', 'Contraband': '#e4ae39',
  // Lowercase variants (database may store these)
  'consumer grade': '#b0c3d9', 'industrial grade': '#5e98d9',
  'mil-spec': '#4b69ff', 'mil_spec': '#4b69ff',
  'restricted': '#8847ff', 'classified': '#d32ce6',
  'covert': '#eb4b4b', 'extraordinary': '#ffd700',
  'contraband': '#e4ae39',
};
const RARITY_ORDER = ['Mil-Spec', 'Restricted', 'Classified', 'Covert', 'Extraordinary'];

// Normalise rarity strings from database to match our RARITY_ORDER keys
function normalizeRarity(r) {
  if (!r) return 'Mil-Spec';
  const lower = r.toLowerCase().replace(/_/g, '-');
  if (lower.includes('mil')) return 'Mil-Spec';
  if (lower.includes('restrict')) return 'Restricted';
  if (lower.includes('classif')) return 'Classified';
  if (lower.includes('covert')) return 'Covert';
  if (lower.includes('extra')) return 'Extraordinary';
  if (lower.includes('contrab')) return 'Contraband';
  if (lower.includes('consumer')) return 'Consumer Grade';
  if (lower.includes('industrial')) return 'Industrial Grade';
  return r; // Return original if no match
}

const CRATE_TIER_NAMES = { 'Mil-Spec': 'MIL-SPEC', 'Restricted': 'RESTRICTED', 'Classified': 'CLASSIFIED', 'Covert': 'COVERT', 'Extraordinary': 'EXTRAORDINARY' };

const DEALER_QUIPS = {
  'Mil-Spec': ["Yeah that one's alright I guess. {price} Fragz.", "Entry level... nothing wrong with that. {price} Fragz.", "Solid starter piece. {price} Fragz, take it or leave it."],
  'Restricted': ["Now you're browsing the good stuff. {price} Fragz.", "Ahh, that one catches the eye doesn't it? {price} Fragz.", "A respectable choice. {price} Fragz and it's yours."],
  'Classified': ["Oh you've got expensive taste... {price} Fragz.", "That piece has a HISTORY. {price} Fragz \u2014 worth every skull.", "Now THAT is a weapon worth wielding. {price} Fragz."],
  'Covert': ["You... you want to see THAT one?! {price} Fragz!!", "My hands are shaking just showing you this. {price} Fragz.", "I risked my LIFE smuggling that in. {price} Fragz. Non-negotiable."],
  'Extraordinary': ["Wait... you can AFFORD that?! {price} Fragz!!!", "I stole this from a dead man's hands. {price} Fragz. Final offer.", "Legends are MADE with this piece. {price} Fragz."],
};
const DEALER_OWNED_QUIPS = ["Have you forgotten already? You bought this from me.", "We did this deal already, friend. Check your inventory.", "This one's already yours... short memory?", "I never sell the same piece twice. You already own it.", "Back for more? This one's already in your collection."];

const FRAGZ_BUNDLES = [
  { amount: 250, price: '\u00a30.99', package: '250_fragz', tag: null },
  { amount: 600, price: '\u00a31.99', package: '600_fragz', tag: 'STARTER' },
  { amount: 1250, price: '\u00a33.99', package: '1250_fragz', tag: null },
  { amount: 3000, price: '\u00a38.99', package: '3000_fragz', tag: 'POPULAR' },
  { amount: 6000, price: '\u00a316.99', package: '6000_fragz', tag: 'BEST VALUE' },
];
const PASS_OPTIONS = [
  { label: '1 MONTH', price: '\u00a34.99', period: '/mo', package: 'premium_monthly', tag: null },
  { label: '3 MONTHS', price: '\u00a311.99', period: '/3mo', package: 'premium_3month', tag: 'SAVE 20%' },
  { label: '1 YEAR', price: '\u00a339.99', period: '/yr', package: 'premium_yearly', tag: 'SAVE 33%' },
];

/* ═══════════════════════════════════════════
   SPARK PARTICLES
   ═══════════════════════════════════════════ */
function SparkBurst({ color, active }) {
  const canvasRef = useRef(null), particlesRef = useRef([]), frameRef = useRef(null), prevActive = useRef(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = 240; canvas.height = 320;
    if (!active) {
      if (prevActive.current) {
        const fadeOut = () => {
          ctx.clearRect(0, 0, 240, 320); let alive = false;
          for (const p of particlesRef.current) {
            if (p.life <= 0) continue; alive = true;
            p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.life -= p.decay * 2;
            ctx.globalAlpha = Math.max(0, p.life * 0.8); ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(0, p.size * p.life), 0, Math.PI * 2); ctx.fill();
          }
          if (alive) frameRef.current = requestAnimationFrame(fadeOut);
        };
        fadeOut();
      }
      prevActive.current = false; return;
    }
    prevActive.current = true;
    const sparks = [];
    for (let i = 0; i < 32; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.2;
      const speed = 1.5 + Math.random() * 4.5;
      sparks.push({ x: 120, y: 240, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 1.5, life: 1, decay: 0.006 + Math.random() * 0.012, size: 1.2 + Math.random() * 2.8, glow: Math.random() > 0.4 });
    }
    particlesRef.current = sparks;
    const animate = () => {
      ctx.clearRect(0, 0, 240, 320); let alive = false;
      for (const p of particlesRef.current) {
        if (p.life <= 0) continue; alive = true;
        p.x += p.vx; p.y += p.vy; p.vy += 0.04; p.vx *= 0.995; p.life -= p.decay;
        ctx.globalAlpha = p.life * 0.9;
        if (p.glow) { ctx.shadowBlur = 10; ctx.shadowColor = color; } else ctx.shadowBlur = 0;
        ctx.fillStyle = color; ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(0, p.size * p.life), 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
      }
      if (alive) frameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [active, color]);
  return <canvas ref={canvasRef} style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 240, height: 320, pointerEvents: 'none', zIndex: 10 }} />;
}

/* ═══════════════════════════════════════════
   AMBIENT EMBERS
   ═══════════════════════════════════════════ */
function AmbientEmbers() {
  const canvasRef = useRef(null), frameRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const colors = ['#E8192C', '#FF6B2B', '#FF6B2B', '#eb4b4b', '#d32ce6'];
    const embers = [];
    for (let i = 0; i < 35; i++) embers.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.25, vy: -(0.12 + Math.random() * 0.45), size: 0.6 + Math.random() * 1.6, color: colors[Math.floor(Math.random() * colors.length)], alpha: 0.1 + Math.random() * 0.3, phase: Math.random() * Math.PI * 2 });
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const e of embers) {
        e.x += e.vx + Math.sin(e.phase) * 0.12; e.y += e.vy; e.phase += 0.015;
        if (e.y < -10) { e.y = canvas.height + 10; e.x = Math.random() * canvas.width; }
        ctx.globalAlpha = e.alpha * (0.6 + Math.sin(e.phase * 2.5) * 0.4);
        ctx.shadowBlur = 5; ctx.shadowColor = e.color; ctx.fillStyle = e.color;
        ctx.beginPath(); ctx.arc(e.x, e.y, e.size, 0, Math.PI * 2); ctx.fill();
      }
      ctx.shadowBlur = 0; ctx.globalAlpha = 1; frameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => { window.removeEventListener('resize', resize); if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
}

/* ═══════════════════════════════════════════
   DEALER CHARACTER
   ═══════════════════════════════════════════ */
function DealerCharacter({ speaking, mood }) {
  const eyeColors = { idle: '#E8192C', 'Mil-Spec': '#4b69ff', Restricted: '#8847ff', Classified: '#d32ce6', Covert: '#eb4b4b', Extraordinary: '#ffd700' };
  const ey = speaking ? eyeColors[mood] || '#FF6B2B' : eyeColors.idle;
  return (
    <svg viewBox="0 0 200 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', filter: speaking ? `drop-shadow(0 0 25px ${ey}55)` : 'none', transition: 'filter 0.5s ease' }}>
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-2.5;0,0" dur="4s" repeatCount="indefinite" />
        <path d="M100 20 C50 20 30 70 30 120 L30 160 C30 160 55 155 100 155 C145 155 170 160 170 160 L170 120 C170 70 150 20 100 20Z" fill="#111" stroke="#1e1e1e" strokeWidth="1.5" />
        <path d="M100 30 C60 30 45 70 45 110 L45 145 C45 145 65 142 100 142 C135 142 155 145 155 145 L155 110 C155 70 140 30 100 30Z" fill="#080808" />
        <path d="M70 40 Q100 25 130 40" fill="none" stroke="#1a1a1a" strokeWidth="0.5" strokeDasharray="4,3" />
        <ellipse cx="80" cy="100" rx="6" ry="3.5" fill={ey} opacity={speaking ? '1' : '0.55'}><animate attributeName="opacity" values={speaking ? '1;0.6;1' : '0.55;0.25;0.55'} dur="3.5s" repeatCount="indefinite" /></ellipse>
        <ellipse cx="120" cy="100" rx="6" ry="3.5" fill={ey} opacity={speaking ? '1' : '0.55'}><animate attributeName="opacity" values={speaking ? '1;0.6;1' : '0.55;0.25;0.55'} dur="3.5s" repeatCount="indefinite" /></ellipse>
        <ellipse cx="80" cy="100" rx="15" ry="9" fill={ey} opacity="0"><animate attributeName="opacity" values={speaking ? '0.2;0.08;0.2' : '0.05;0.01;0.05'} dur="3.5s" repeatCount="indefinite" /></ellipse>
        <ellipse cx="120" cy="100" rx="15" ry="9" fill={ey} opacity="0"><animate attributeName="opacity" values={speaking ? '0.2;0.08;0.2' : '0.05;0.01;0.05'} dur="3.5s" repeatCount="indefinite" /></ellipse>
        <path d="M30 160 L15 310 L70 310 L80 220 L100 230 L120 220 L130 310 L185 310 L170 160 C170 160 145 155 100 155 C55 155 30 160 30 160Z" fill="#0d0d0d" stroke="#1a1a1a" strokeWidth="1" />
        <path d="M75 160 L90 230 L100 200 L110 230 L125 160" fill="none" stroke="#1a1a1a" strokeWidth="1.5" />
        <line x1="38" y1="210" x2="162" y2="210" stroke="#222" strokeWidth="3" />
        <rect x="93" y="205" width="14" height="10" rx="2" fill="#2a2a2a" stroke="#3a3a3a" strokeWidth="0.5" />
        <line x1="48" y1="255" x2="62" y2="310" stroke="#161616" strokeWidth="0.7" />
        <line x1="152" y1="255" x2="138" y2="310" stroke="#161616" strokeWidth="0.7" />
      </g>
    </svg>
  );
}

/* ═══════════════════════════════════════════
   MAIN SHOP COMPONENT
   ═══════════════════════════════════════════ */
export default function Shop() {
  const { user, stats } = useAuth();
  const [shopItems, setShopItems] = useState([]);
  const [ownedSkinIds, setOwnedSkinIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [openCrate, setOpenCrate] = useState(null);
  const [dealerMessage, setDealerMessage] = useState('');
  const [dealerMood, setDealerMood] = useState('idle');
  const [showDealer, setShowDealer] = useState(false);
  const [buyConfirm, setBuyConfirm] = useState(null);
  const [buying, setBuying] = useState(false);
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0 });
  const [nextRefreshAt, setNextRefreshAt] = useState(null);
  const [hoveredBundle, setHoveredBundle] = useState(null);
  const [hoveredPass, setHoveredPass] = useState(null);
  const [hoveredBuyBtn, setHoveredBuyBtn] = useState(false);
  const [hoveredConfirm, setHoveredConfirm] = useState(null);
  const [hoveredKit, setHoveredKit] = useState(null);
  const [stattrakKits, setStattrakKits] = useState(stats?.stattrak_kits || 0);
  const [nametagKits, setNametagKits] = useState(stats?.nametag_kits || 0);
  const [userFragz, setUserFragz] = useState(stats?.fragz || 0);
  const dealerTimeoutRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Detect return from Stripe checkout
  useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      setShowPaymentSuccess(true);
      // Clean URL
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('payment');
      setSearchParams(nextSearchParams, { replace: true });
      // Refresh fragz balance
      getMe().then(res => setUserFragz(res.data.stats?.fragz || 0)).catch(() => {});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setUserFragz(stats?.fragz || 0);
    setStattrakKits(stats?.stattrak_kits || 0);
    setNametagKits(stats?.nametag_kits || 0);
  }, [stats]);

  // Fetch shop + inventory SEPARATELY
  useEffect(() => {
    const load = async () => {
      try {
        const shopRes = await getShop();
        console.log('[Shop] GET /shop response:', shopRes.data);
        const items = shopRes.data?.items || shopRes.data || [];
        setShopItems(items);
        setNextRefreshAt(shopRes.data?.next_refresh_at || shopRes.data?.available_until || null);
        if (items.length === 0) setLoadError('The daily shop is being prepared. Please check back shortly.');
      } catch (err) {
        console.error('[Shop] GET /shop failed:', err.response?.data || err.message);
        setLoadError('Failed to load shop: ' + (err.response?.data?.detail || err.message));
      }
      // Owned detection (separate try so it can't break shop loading)
      try {
        if (!user) {
          setOwnedSkinIds(new Set());
          setLoading(false);
          return;
        }
        const invResp = await getInventory();
        const invData = invResp.data;
        const inv = invData?.inventory || [];
        setOwnedSkinIds(new Set(inv.map(i => i.skin_id)));
        console.log('[Shop] Owned skin IDs:', inv.map(i => i.skin_id));
      } catch (e) { console.log('[Shop] Inventory fetch failed (non-critical):', e); }
      setLoading(false);
    };
    load();
  }, [user]);

  // Countdown to the backend-owned refresh time, falling back to midnight UTC.
  useEffect(() => {
    const calc = () => {
      const n = new Date();
      const refresh = nextRefreshAt ? new Date(nextRefreshAt) : new Date(n);
      if (!nextRefreshAt) {
        refresh.setUTCDate(refresh.getUTCDate() + 1);
        refresh.setUTCHours(0, 0, 0, 0);
      }
      const d = Math.max(0, refresh - n);
      return { h: Math.floor(d / 3600000), m: Math.floor((d % 3600000) / 60000), s: Math.floor((d % 60000) / 1000) };
    };
    setCountdown(calc()); const t = setInterval(() => setCountdown(calc()), 1000); return () => clearInterval(t);
  }, [nextRefreshAt]);

  const pad = n => String(n).padStart(2, '0');

  // Build crate data
  // Build crates — one per rarity tier, always in fixed order
  const cratesByRarity = {};
  shopItems.forEach(item => {
    const skin = item.skins || item;
    const rarity = normalizeRarity(skin.rarity);
    const color = RARITY_COLORS[rarity] || '#4b69ff';
    const skinId = item.skin_id || item.id;
    if (!cratesByRarity[rarity]) {
      cratesByRarity[rarity] = { id: skinId, name: skin.name, rarity, color, price: item.price_fragz, image_url: skin.image_url, float: 0.001, wear: 'Factory New', tierName: CRATE_TIER_NAMES[rarity] || rarity.toUpperCase(), owned: ownedSkinIds.has(skinId) };
    }
  });
  // Always 5 crates in fixed ascending order
  const crates = RARITY_ORDER.map(r => {
    if (cratesByRarity[r]) return cratesByRarity[r];
    return { id: null, name: 'Coming Soon', rarity: r, color: RARITY_COLORS[r], price: 0, image_url: null, float: 0, wear: '-', tierName: CRATE_TIER_NAMES[r], owned: false };
  });

  const handleBgClick = useCallback(() => {
    if (openCrate !== null) { setOpenCrate(null); if (dealerTimeoutRef.current) clearTimeout(dealerTimeoutRef.current); setTimeout(() => { setShowDealer(false); setDealerMood('idle'); }, 300); }
  }, [openCrate]);

  const handleCrateClick = useCallback((index, e) => {
    e.stopPropagation();
    const crate = crates[index];
    if (!crate || !crate.id) return;
    if (openCrate === index) { setOpenCrate(null); setTimeout(() => { setShowDealer(false); setDealerMood('idle'); }, 300); return; }
    setOpenCrate(index);
    let quip;
    if (crate.owned) { quip = DEALER_OWNED_QUIPS[Math.floor(Math.random() * DEALER_OWNED_QUIPS.length)]; }
    else { const qs = DEALER_QUIPS[crate.rarity] || DEALER_QUIPS['Mil-Spec']; quip = qs[Math.floor(Math.random() * qs.length)].replace('{price}', crate.price.toLocaleString()); }
    if (dealerTimeoutRef.current) clearTimeout(dealerTimeoutRef.current);
    setShowDealer(false); setDealerMood(crate.rarity);
    dealerTimeoutRef.current = setTimeout(() => { setDealerMessage(quip); setShowDealer(true); }, 450);
  }, [openCrate, crates]);

  const handleBuy = (crate, e) => { e.stopPropagation(); setBuyConfirm(crate); };
  const confirmBuy = async () => {
    if (!buyConfirm || buying) return; setBuying(true);
    try {
      await buySkin(buyConfirm.id);
      const res = await getMe(); setUserFragz(res.data.stats?.fragz || 0);
      setOwnedSkinIds(prev => new Set([...prev, buyConfirm.id]));
      setBuyConfirm(null); setDealerMessage('Pleasure doing business with you...'); setDealerMood('idle'); setShowDealer(true); setTimeout(() => setShowDealer(false), 2500);
    } catch (err) {
      console.error('[Shop] Buy failed:', err.response?.data || err); setBuyConfirm(null);
      setDealerMessage(err.response?.data?.detail || 'Something went wrong...'); setShowDealer(true); setTimeout(() => setShowDealer(false), 2500);
    } finally { setBuying(false); }
  };

  const handleBuyFragz = async (bundle, e) => {
    e.stopPropagation();
    try {
      console.log('[Shop] Creating checkout:', bundle.package);
      const res = await createCheckout(bundle.package);
      console.log('[Shop] Checkout response:', res.data);
      const url = res.data?.checkout_url || res.data?.url;
      if (url) window.location.href = url;
      else console.error('[Shop] No URL in checkout response:', res.data);
    } catch (err) { console.error('[Shop] Checkout failed:', err.response?.status, err.response?.data || err.message); }
  };

  const handleBuyPass = async (pass, e) => {
    e.stopPropagation();
    try {
      console.log('[Shop] Creating pass checkout:', pass.package);
      const res = await createCheckout(pass.package);
      console.log('[Shop] Pass checkout response:', res.data);
      const url = res.data?.checkout_url || res.data?.url;
      if (url) window.location.href = url;
      else console.error('[Shop] No URL in pass response:', res.data);
    } catch (err) { console.error('[Shop] Pass checkout failed:', err.response?.status, err.response?.data || err.message); }
  };

  // Buy kit (StatTrak or Nametag)
  const handleBuyKit = async (type, e) => {
    e.stopPropagation();
    try {
      await buyKit(type);
      const r = await getMe();
      setUserFragz(r.data.stats?.fragz || 0);
      setStattrakKits(r.data.stats?.stattrak_kits || 0);
      setNametagKits(r.data.stats?.nametag_kits || 0);
      setDealerMessage(type === 'stattrak' ? 'StatTrak kit acquired. Use it wisely.' : 'Nametag kit acquired. Make it personal.');
      setDealerMood('idle');
      setShowDealer(true);
      setTimeout(() => setShowDealer(false), 2500);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Purchase failed';
      alert(msg);
    }
  };

  const moodColor = { Covert: '#eb4b4b', Classified: '#d32ce6', Restricted: '#8847ff', Extraordinary: '#ffd700' }[dealerMood] || '#FF6B2B';

  return (
    <div style={st.root} onClick={handleBgClick}>
      <style>{`
        html, body, #root{height:auto!important;min-height:100%;overflow-y:auto!important;overflow-x:hidden!important}
        @keyframes rp-bub{from{opacity:0;transform:translateX(-50%) scale(.8) translateY(10px)}to{opacity:1;transform:translateX(-50%) scale(1) translateY(0)}}
        @keyframes rp-shim{to{transform:rotate(360deg)}}
        @keyframes rp-fi{from{opacity:0}to{opacity:1}}
        @keyframes rp-mi{from{opacity:0;transform:scale(.9)}to{opacity:1;transform:scale(1)}}
        @keyframes rp-p{0%,100%{opacity:.4}50%{opacity:1}}
        @keyframes rp-page-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @media (prefers-reduced-motion: reduce){*{animation-duration:1ms!important;transition-duration:1ms!important}}
      `}</style>
      <AmbientEmbers />
      <div style={st.content}>
        <section style={st.hero}>
          <div style={st.heroCopy}>
            <Eyebrow>Daily skin market</Eyebrow>
            <h1 style={st.title}>BLACKSITE SHOP</h1>
            <p style={st.subtitle}>
              Press a sealed crate to reveal today&apos;s Factory New skin. Same rotation system, rebuilt to match the new Repeaked interface.
            </p>
          </div>
        </section>

        {loadError && <div style={st.errorBox}>{loadError}</div>}

        {loading ? (
          <div style={st.loading}>
            <Diamond size={30} withDot />
            <span style={{ animation: 'rp-p 1.5s ease-in-out infinite' }}>SYNCING MARKET ROTATION</span>
          </div>
        ) : (
          <section style={st.stagePanel}>
            <div style={st.stageHead}>
              <div>
                <Eyebrow>Reveal crates</Eyebrow>
                <h2 style={st.stageTitle}>TODAY&apos;S SEALED STOCK</h2>
              </div>
              <div style={st.stageTools}>
                <div style={st.fragzBar}><FragzIcon size={28} /> {userFragz.toLocaleString()} <span>FRAGZ</span></div>
                <div style={st.timer}>
                  <span style={st.timerLabel}>Refresh</span>
                  <span style={st.timerValue}><span style={st.timerAcc}>{pad(countdown.h)}</span>:{pad(countdown.m)}:{pad(countdown.s)}</span>
                </div>
              </div>
            </div>
            <p style={st.stageCopy}>Open one crate at a time. The dealer reveals the current shop skin, price, rarity and ownership state.</p>

            <div style={st.cSec}>
              <div style={st.cRow}>
                {crates.map((c, i) => {
                  const isOpen = openCrate === i;
                  const canAfford = userFragz >= c.price;
                  const ph = !c.id;
                  return (
                    <div key={c.id || `ph-${i}`} style={{ ...st.cW, opacity: ph ? 0.45 : 1 }} onClick={e => handleCrateClick(i, e)}>
                      <SparkBurst color={c.color} active={isOpen} />
                      <div style={st.crateRarity}>
                        <span style={{ ...st.rarityDot, background: c.color, boxShadow: `0 0 14px ${c.color}` }} />
                        {c.tierName}
                      </div>
                      <div style={{ ...st.sRev, opacity: isOpen ? 1 : 0, transform: isOpen ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)', pointerEvents: isOpen ? 'auto' : 'none' }}>
                        <div style={{ ...st.sCard, borderColor: c.color, boxShadow: `0 22px 70px rgba(0,0,0,.78),0 0 55px ${c.color}26` }} onClick={e => e.stopPropagation()}>
                          {c.owned && <div style={st.oBan}><span style={st.oTxt}>OWNED</span></div>}
                          <div style={st.shW}><div style={{ ...st.shD, background: `conic-gradient(from 0deg,transparent 0%,transparent 70%,${c.color} 75%,transparent 80%)` }} /></div>
                          <div style={{ ...st.sImg, border: `1px solid ${c.color}44`, opacity: c.owned ? 0.55 : 1 }}>
                            {c.image_url ? <img src={c.image_url} alt={c.name} style={st.sImgT} /> : <span style={st.noImage}>No image</span>}
                          </div>
                          <div style={st.sNm}>{c.name}</div>
                          <div style={{ ...st.rLbl, color: c.color }}>{c.rarity}</div>
                          {!c.owned && (
                            <div style={st.flSec}>
                              <div style={st.flH}><span style={{ ...st.flW, color: C.green }}>{c.wear}</span><span style={st.flV}>{c.float.toFixed(4)}</span></div>
                              <div style={st.flTr}>
                                <div style={{ ...st.flSg, background: '#4ade80', left: '0%', width: '7%', borderRadius: '3px 0 0 3px' }} />
                                <div style={{ ...st.flSg, background: '#60a5fa', left: '7%', width: '8%' }} />
                                <div style={{ ...st.flSg, background: '#facc15', left: '15%', width: '23%' }} />
                                <div style={{ ...st.flSg, background: '#fb923c', left: '38%', width: '7%' }} />
                                <div style={{ ...st.flSg, background: '#ef4444', left: '45%', width: '55%', borderRadius: '0 3px 3px 0' }} />
                                <div style={{ ...st.flNd, left: `${c.float * 100}%` }} />
                              </div>
                              <div style={st.flHint}>Pristine shop stock · random drops stay separate</div>
                            </div>
                          )}
                          <div style={{ ...st.sPr, color: c.owned ? C.textVeryDim : canAfford ? C.orange : C.red }}><FragzIcon size={30} /> {c.price.toLocaleString()}</div>
                          {c.owned ? <button style={st.bDis}>ALREADY OWNED</button> : canAfford ? <button style={hoveredBuyBtn === i ? { ...st.bBtn, ...st.bBH } : st.bBtn} onMouseEnter={() => setHoveredBuyBtn(i)} onMouseLeave={() => setHoveredBuyBtn(false)} onClick={e => handleBuy(c, e)}>BUY NOW</button> : <button style={st.bDis}>NOT ENOUGH FRAGZ</button>}
                        </div>
                      </div>
                      <div style={{ ...st.lid, transform: isOpen ? 'translateY(-36px) scaleX(.78)' : 'none', opacity: isOpen ? 0.25 : 1, borderColor: isOpen ? `${c.color}aa` : C.borderBright }}>
                        <div style={{ ...st.lidGlow, background: `linear-gradient(90deg, transparent, ${c.color}88, transparent)` }} />
                        <div style={st.lidGrip} />
                      </div>
                      <div style={{ ...st.cBd, borderColor: isOpen ? `${c.color}aa` : C.borderBright, boxShadow: isOpen ? `0 16px 42px rgba(0,0,0,.72), 0 0 42px ${c.color}26, inset 0 0 0 1px ${c.color}22` : st.cBd.boxShadow }}>
                        <div style={{ ...st.caseGlow, background: `radial-gradient(circle at 50% 22%, ${c.color}30 0%, transparent 48%)` }} />
                        <div style={st.cGr} />
                        <div style={{ ...st.vaultBeam, background: `linear-gradient(180deg, ${c.color}00, ${c.color}66, ${c.color}00)` }} />
                        <div style={{ ...st.vaultRing, borderColor: `${c.color}80`, boxShadow: `0 0 30px ${c.color}22` }}>
                          <Diamond size={34} color={c.color} dotColor={c.color} withDot />
                        </div>
                        <div style={st.vaultBrand}>REPEAKED</div>
                        <div style={st.vaultSerial}>SEALED DROP {String(i + 1).padStart(2, '0')}</div>
                        <div style={{ ...st.vaultSide, left: 10 }} />
                        <div style={{ ...st.vaultSide, right: 10 }} />
                        <div style={{ ...st.cLb, color: c.color, opacity: isOpen ? 0 : 0.52 }}>{c.tierName}</div>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: c.color, opacity: isOpen ? 1 : 0.62, boxShadow: `0 0 15px ${c.color},0 0 30px ${c.color}`, transition: 'opacity .3s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={st.dW}>
                {showDealer && <div style={{ ...st.bbl, borderColor: moodColor, boxShadow: `0 0 34px ${moodColor}30`, animation: 'rp-bub .4s cubic-bezier(.34,1.56,.64,1) forwards' }}>{dealerMessage}<div style={{ ...st.bblA, borderTopColor: moodColor }} /></div>}
                <div style={st.dFig}><DealerCharacter speaking={showDealer} mood={dealerMood} /></div>
              </div>
            </div>
            <div style={st.floor} />
          </section>
        )}

        <SectionHeader kicker="Balance" title="GET FRAGZ">Top up for skins, kits and pass upgrades. Pricing is unchanged from the current shop.</SectionHeader>
        <div style={st.bGr}>
          {FRAGZ_BUNDLES.map((b, i) => (
            <div key={i} style={hoveredBundle === i ? { ...st.bCd, ...st.bCdH } : st.bCd} onMouseEnter={() => setHoveredBundle(i)} onMouseLeave={() => setHoveredBundle(null)} onClick={e => handleBuyFragz(b, e)}>
              {b.tag && <div style={st.bTg}>{b.tag}</div>}
              <div style={st.bAm}>{b.amount.toLocaleString()}</div>
              <div style={st.bLb}><FragzIcon size={24} /> Fragz</div>
              <div style={st.bPrc}>{b.price}</div>
              <div style={st.bPer}>{(parseFloat(b.price.replace(/[^0-9.]/g, '')) / b.amount * 1000).toFixed(1)}p per 1K</div>
            </div>
          ))}
        </div>

        <SectionHeader kicker="Workshop" title="UPGRADE KITS">Personalise your owned skins with tracking and custom names.</SectionHeader>
        <div style={st.kitGrid}>
          <div
            style={hoveredKit === 'stattrak' ? { ...st.kitCard, ...st.kitCardH } : st.kitCard}
            onMouseEnter={() => setHoveredKit('stattrak')}
            onMouseLeave={() => setHoveredKit(null)}
            onClick={e => handleBuyKit('stattrak', e)}
          >
            <div style={st.kitIcon}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke={C.orange} strokeWidth="1.5" />
                <path d="M7 13l3 3 7-7" stroke={C.orange} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={st.kitName}>STATTRAK KIT</div>
            <div style={st.kitDesc}>Track your kills on any weapon skin</div>
            <div style={st.kitPrice}><FragzIcon size={22} /> 200</div>
            <div style={st.kitOwned}>You own: <span style={{ color: C.orange }}>{stattrakKits}</span></div>
          </div>

          <div
            style={hoveredKit === 'nametag' ? { ...st.kitCard, ...st.kitCardH } : st.kitCard}
            onMouseEnter={() => setHoveredKit('nametag')}
            onMouseLeave={() => setHoveredKit(null)}
            onClick={e => handleBuyKit('nametag', e)}
          >
            <div style={st.kitIcon}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h16v12H4z" stroke={C.orange} strokeWidth="1.5" />
                <path d="M7 10h10M7 14h6" stroke={C.orange} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div style={st.kitName}>NAMETAG KIT</div>
            <div style={st.kitDesc}>Give your skin a custom name</div>
            <div style={st.kitPrice}><FragzIcon size={22} /> 100</div>
            <div style={st.kitOwned}>You own: <span style={{ color: C.orange }}>{nametagKits}</span></div>
          </div>
        </div>

        <SectionHeader kicker="Premium" title="PREMIUM PASS">Optional support perks for players who want boosted cosmetic progression.</SectionHeader>
        <div style={st.pGr}>
          {PASS_OPTIONS.map((p, i) => (
            <div key={i} style={hoveredPass === i ? { ...st.pCd, ...st.pCdH, ...(i === 1 ? st.pCdF : {}) } : { ...st.pCd, ...(i === 1 ? st.pCdF : {}) }} onMouseEnter={() => setHoveredPass(i)} onMouseLeave={() => setHoveredPass(null)} onClick={e => e.stopPropagation()}>
              {p.tag && <div style={st.pTg}>{p.tag}</div>}
              <div style={st.pLb}>{p.label}</div>
              <div style={st.pPr}>{p.price}<span style={st.pPd}>{p.period}</span></div>
              <div style={st.pPerks}>
                {['3x skin drop rate multiplier', 'Better odds for rarer game drops', 'Premium badge on profile', 'Priority matchmaking queue'].map((pk, j) => (
                  <div key={j} style={st.pPk}><div style={st.pPkD} />{pk}</div>
                ))}
              </div>
              <button style={st.pBtn} onClick={e => handleBuyPass(p, e)}>GET PASS</button>
            </div>
          ))}
        </div>
      </div>

      {/* PAYMENT SUCCESS POPUP */}
      {showPaymentSuccess && (
        <div style={st.mOv} onClick={() => setShowPaymentSuccess(false)}>
          <div style={{ ...st.mBx, padding: 40 }} onClick={e => e.stopPropagation()}>
            {/* Glow */}
            <div style={{ position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)', width: 300, height: 150, background: 'radial-gradient(ellipse, rgba(78,200,100,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
            {/* Checkmark */}
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(78,200,100,0.1)', border: '2px solid rgba(78,200,100,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#4ec864" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 3, color: '#F0F2F5', marginBottom: 4 }}>PURCHASE COMPLETE</div>
            <p style={{ color: '#888', fontSize: 14, margin: '8px 0 4px', lineHeight: 1.6 }}>Thank you for supporting Repeaked!</p>
            <p style={{ color: '#555', fontSize: 13, margin: '0 0 20px' }}>Your Fragz have been added to your account.</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20, padding: '12px 20px', background: 'rgba(255,107,43,0.06)', border: '1px solid rgba(255,107,43,0.15)', borderRadius: 8 }}>
              <FragzIcon size={32} />
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, letterSpacing: 2, color: '#FF6B2B' }}>{userFragz.toLocaleString()}</span>
              <span style={{ fontSize: 11, letterSpacing: 2, color: '#666', fontWeight: 700 }}>FRAGZ</span>
            </div>
            <button
              style={{ ...st.mCf, width: '100%', padding: 12 }}
              onClick={() => setShowPaymentSuccess(false)}
            >CONTINUE SHOPPING</button>
          </div>
        </div>
      )}

      {/* BUY MODAL */}
      {buyConfirm && (
        <div style={st.mOv} onClick={() => setBuyConfirm(null)}>
          <div style={st.mBx} onClick={e => e.stopPropagation()}>
            <div style={st.mNm}>{buyConfirm.name}</div>
            <p style={st.mTx}>Purchase for <span style={{ color: '#FF6B2B', fontWeight: 700 }}><FragzIcon size={28} /> {buyConfirm.price.toLocaleString()} Fragz</span>?</p>
            <p style={st.mBl}>Your balance: <FragzIcon size={24} /> {userFragz.toLocaleString()} Fragz{userFragz >= buyConfirm.price && <span style={{ color: '#444' }}> (<FragzIcon size={24} /> {(userFragz - buyConfirm.price).toLocaleString()} remaining)</span>}</p>
            <div style={st.mAc}>
              <button style={hoveredConfirm === 'cancel' ? { ...st.mCn, borderColor: '#555', color: '#F0F2F5' } : st.mCn} onMouseEnter={() => setHoveredConfirm('cancel')} onMouseLeave={() => setHoveredConfirm(null)} onClick={() => setBuyConfirm(null)}>CANCEL</button>
              <button style={hoveredConfirm === 'confirm' ? { ...st.mCf, transform: 'translateY(-1px)', boxShadow: '0 4px 15px rgba(232,25,44,.4)' } : st.mCf} onMouseEnter={() => setHoveredConfirm('confirm')} onMouseLeave={() => setHoveredConfirm(null)} onClick={confirmBuy} disabled={buying}>{buying ? 'PURCHASING...' : 'CONFIRM'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════ */
const st = {
  root: {
    position: 'relative',
    minHeight: '100vh',
    overflowX: 'hidden',
    overflowY: 'visible',
    fontFamily: F.body,
    color: C.text,
    background: `radial-gradient(circle at 18% 0%, ${C.red}22 0, transparent 30%), radial-gradient(circle at 82% 12%, ${C.orange}18 0, transparent 32%), linear-gradient(180deg, ${C.bg} 0%, #090705 100%)`,
  },
  content: { position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '34px 24px 84px', animation: 'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both' },
  hero: { display: 'block', marginBottom: 24 },
  heroCopy: {
    position: 'relative',
    overflow: 'hidden',
    background: `linear-gradient(135deg, ${C.card} 0%, ${C.cardAlt} 100%)`,
    borderTop: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    borderLeft: `2px solid ${C.red}`,
    padding: '30px clamp(22px, 4vw, 44px)',
    boxShadow: '0 24px 70px rgba(0,0,0,.32)',
  },
  title: { fontFamily: F.display, fontSize: 'clamp(54px, 8vw, 106px)', letterSpacing: 3, lineHeight: 0.86, margin: '16px 0 14px', color: C.text, textTransform: 'uppercase' },
  subtitle: { maxWidth: 660, color: C.textDim, fontSize: 17, lineHeight: 1.55, margin: 0, letterSpacing: 0.2 },
  timer: { display: 'flex', alignItems: 'baseline', gap: 10, padding: '10px 14px', background: 'rgba(255,255,255,.035)', border: `1px solid ${C.borderBright}` },
  timerLabel: { fontSize: 10, fontWeight: 800, color: C.textDim, letterSpacing: 2.5, textTransform: 'uppercase' },
  timerValue: { fontFamily: F.display, fontSize: 25, letterSpacing: 2, color: C.orange, lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  timerAcc: { color: C.red },
  fragzBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'rgba(232,25,44,.08)', border: `1px solid ${C.red}55`, fontFamily: F.display, fontSize: 25, letterSpacing: 1.5, color: C.orange, lineHeight: 1 },
  errorBox: { margin: '0 0 18px', padding: '14px 16px', color: '#ff9aa4', background: 'rgba(232,25,44,.09)', border: `1px solid ${C.red}55`, fontSize: 13, fontWeight: 700, letterSpacing: 0.4 },
  loading: { minHeight: 420, display: 'flex', flexDirection: 'column', gap: 18, alignItems: 'center', justifyContent: 'center', background: 'rgba(17,17,17,.6)', border: `1px solid ${C.border}`, fontFamily: F.display, fontSize: 26, letterSpacing: 3, color: C.textDim },
  stagePanel: { position: 'relative', background: 'linear-gradient(180deg, rgba(17,17,17,.86), rgba(12,9,5,.72))', border: `1px solid ${C.border}`, padding: '24px clamp(16px, 3vw, 30px) 28px', boxShadow: '0 24px 80px rgba(0,0,0,.34)' },
  stageHead: { display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 10 },
  stageTitle: { fontFamily: F.display, color: C.text, fontSize: 42, letterSpacing: 2.5, lineHeight: 0.95, margin: '10px 0 0', textTransform: 'uppercase' },
  stageCopy: { maxWidth: 560, color: C.textDim, margin: '0 0 28px', fontSize: 14, lineHeight: 1.55 },
  stageTools: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' },
  cSec: { display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 18, marginTop: 16, position: 'relative', minHeight: 420, paddingBottom: 18 },
  cRow: { display: 'flex', gap: 12, flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'flex-end', flex: '0 1 auto', minWidth: 0 },
  cW: { position: 'relative', cursor: 'pointer', width: 142, transition: 'transform .25s ease, opacity .25s ease', paddingTop: 24, flex: '0 0 142px' },
  crateRarity: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 20, marginBottom: 8, fontSize: 10, fontWeight: 800, letterSpacing: 2, color: C.textDim, textTransform: 'uppercase' },
  rarityDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  cBd: { width: 142, height: 176, position: 'relative', background: 'linear-gradient(180deg,#17110D 0%,#0C0905 58%,#050403 100%)', border: `1.5px solid ${C.borderBright}`, overflow: 'hidden', clipPath: 'polygon(10% 0, 90% 0, 100% 10%, 100% 90%, 90% 100%, 10% 100%, 0 90%, 0 10%)', boxShadow: '0 16px 42px rgba(0,0,0,.68), inset 0 1px 0 rgba(255,255,255,.05)', transition: 'border-color .25s ease, box-shadow .25s ease, transform .25s ease' },
  caseGlow: { position: 'absolute', inset: 0, opacity: 0.9, pointerEvents: 'none' },
  cGr: { position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none', background: 'linear-gradient(135deg,rgba(255,255,255,.06) 0%,transparent 28%,transparent 70%,rgba(0,0,0,.35) 100%),repeating-linear-gradient(2deg,transparent,transparent 7px,rgba(255,107,43,.16) 7px,rgba(255,107,43,.16) 8px),repeating-linear-gradient(178deg,transparent,transparent 11px,rgba(232,25,44,.12) 11px,rgba(232,25,44,.12) 12px)' },
  vaultBeam: { position: 'absolute', left: '50%', top: 18, bottom: 18, width: 2, transform: 'translateX(-50%)', opacity: 0.9, filter: 'blur(.2px)' },
  vaultRing: { position: 'absolute', left: '50%', top: '44%', transform: 'translate(-50%, -50%)', width: 68, height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(circle,rgba(255,255,255,.055),rgba(0,0,0,.48))', border: '1px solid', zIndex: 3, clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)' },
  vaultBrand: { position: 'absolute', left: -34, top: 77, width: 118, transform: 'rotate(-90deg)', textAlign: 'center', fontFamily: F.display, fontSize: 13, letterSpacing: 3, color: 'rgba(240,242,245,.2)', zIndex: 3 },
  vaultSerial: { position: 'absolute', bottom: 18, left: 0, right: 0, textAlign: 'center', fontFamily: F.body, fontSize: 8, fontWeight: 800, letterSpacing: 1.8, color: C.textVeryDim, zIndex: 4 },
  vaultSide: { position: 'absolute', top: 18, bottom: 18, width: 5, background: 'linear-gradient(180deg,#3e3329,#15100b)', borderLeft: '1px solid rgba(255,255,255,.08)', borderRight: '1px solid rgba(0,0,0,.55)', boxShadow: 'inset 0 0 10px rgba(0,0,0,.45)' },
  cPl: { position: 'absolute', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,.045)' },
  brk: { position: 'absolute', width: 19, height: 19, borderColor: '#44382d', borderStyle: 'solid', borderWidth: 0, opacity: 0.65 },
  riv: { position: 'absolute', width: 5, height: 5, borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%,#6a5d51,#2a2520)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,.14),0 1px 2px rgba(0,0,0,.7)' },
  clsp: { position: 'absolute', left: '50%', top: -1, transform: 'translateX(-50%)', width: 42, height: 16, zIndex: 4, background: 'linear-gradient(180deg,#554537,#2a231b)', border: '1px solid #6a5746', borderTop: 'none', boxShadow: '0 2px 8px rgba(0,0,0,.55)' },
  clspD: { position: 'absolute', left: '50%', top: 5, transform: 'translateX(-50%)', width: 5, height: 5, borderRadius: '50%', background: 'radial-gradient(circle at 40% 40%,#776a5d,#302820)' },
  cLb: { position: 'absolute', bottom: 40, left: 0, right: 0, textAlign: 'center', fontFamily: F.display, fontSize: 12, letterSpacing: 2.4, zIndex: 5, transition: 'opacity .3s', textShadow: '0 2px 8px rgba(0,0,0,.85)' },
  lid: { width: 122, height: 16, marginLeft: 10, position: 'relative', background: 'linear-gradient(180deg,#3a2f25 0%,#1b140f 100%)', border: `1.5px solid ${C.borderBright}`, marginBottom: -1, zIndex: 4, transition: 'all .45s cubic-bezier(.34,1.56,.64,1)', transformOrigin: 'center bottom', boxShadow: '0 -5px 16px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.06)', overflow: 'hidden', clipPath: 'polygon(8% 0, 92% 0, 100% 100%, 0 100%)' },
  lidGlow: { position: 'absolute', left: 20, right: 20, top: 0, height: 2, opacity: 0.8 },
  lidGrip: { position: 'absolute', left: '50%', top: 5, transform: 'translateX(-50%)', width: 46, height: 5, background: 'repeating-linear-gradient(90deg,#5b4a3b 0,#5b4a3b 4px,#241b14 4px,#241b14 8px)', border: '1px solid rgba(255,255,255,.05)', boxShadow: 'inset 0 1px 3px rgba(0,0,0,.6)' },
  lRiv: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 5, height: 5, borderRadius: '50%', background: 'radial-gradient(circle at 35% 35%,#6a5d51,#2a2520)' },
  sRev: { position: 'absolute', bottom: 180, left: '50%', width: 196, paddingBottom: 8, zIndex: 20, transition: 'all .5s cubic-bezier(.34,1.56,.64,1)' },
  sCard: { background: 'linear-gradient(180deg,#121212 0%,#0A0A0A 100%)', border: '1.5px solid', padding: 14, textAlign: 'center', position: 'relative', overflow: 'hidden' },
  shW: { position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' },
  shD: { position: 'absolute', top: '-50%', left: '-50%', width: '200%', height: '200%', opacity: 0.08, animation: 'rp-shim 6s linear infinite' },
  sImg: { width: '100%', height: 104, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(circle at 50% 45%, rgba(255,255,255,.08), rgba(0,0,0,.58) 66%)', overflow: 'hidden' },
  noImage: { color: C.textVeryDim, fontSize: 12, letterSpacing: 1 },
  sImgT: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'drop-shadow(0 12px 20px rgba(0,0,0,.55))' },
  sNm: { fontFamily: F.display, fontSize: 18, letterSpacing: 1.2, margin: '0 0 3px', color: C.text, textTransform: 'uppercase' },
  rLbl: { fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 7, fontWeight: 800 },
  oBan: { position: 'absolute', top: 17, right: -36, transform: 'rotate(45deg)', background: C.red, padding: '4px 42px', zIndex: 10 },
  oTxt: { fontFamily: F.display, fontSize: 11, letterSpacing: 2, color: 'white' },
  flSec: { margin: '7px 0 3px', padding: '8px 10px', background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.06)' },
  flH: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  flW: { fontFamily: F.display, fontSize: 13, letterSpacing: 1.5 },
  flV: { fontFamily: F.body, fontSize: 12, color: C.textDim, fontWeight: 700 },
  flTr: { position: 'relative', width: '100%', height: 5, borderRadius: 3, background: '#1a1a1a', marginBottom: 5 },
  flSg: { position: 'absolute', top: 0, height: '100%', opacity: 0.74 },
  flNd: { position: 'absolute', top: -3, width: 2, height: 11, background: '#fff', borderRadius: 1, transform: 'translateX(-50%)', boxShadow: '0 0 6px rgba(255,255,255,.5)' },
  flHint: { fontSize: 9, color: C.textVeryDim, textAlign: 'center', letterSpacing: 0.6, fontStyle: 'italic' },
  sPr: { fontFamily: F.display, fontSize: 24, margin: '8px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  bBtn: { width: '100%', minHeight: 40, padding: 10, border: 'none', cursor: 'pointer', fontFamily: F.display, fontSize: 15, letterSpacing: 2.2, background: `linear-gradient(135deg,${C.red},${C.orange})`, color: 'white', transition: 'all .18s ease', boxShadow: `0 7px 22px ${C.redGlow}`, textTransform: 'uppercase' },
  bBH: { transform: 'translateY(-1px)', boxShadow: `0 10px 28px ${C.redGlow}` },
  bDis: { width: '100%', minHeight: 40, padding: 10, border: `1px solid ${C.borderBright}`, cursor: 'default', fontFamily: F.display, fontSize: 15, letterSpacing: 2, background: '#2a2520', color: C.textDim, opacity: 0.82 },
  dW: { position: 'relative', width: 152, height: 264, flexShrink: 0, marginLeft: 4 },
  dFig: { width: '100%', height: '100%', opacity: 0.95 },
  bbl: { position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, background: '#0e0e0e', border: '1px solid', padding: '14px 18px', width: 248, fontSize: 14, lineHeight: 1.55, color: C.text, fontWeight: 600, zIndex: 30, textAlign: 'center' },
  bblA: { position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', border: '8px solid transparent' },
  floor: { width: '100%', maxWidth: 980, height: 1, margin: '0 auto', background: `linear-gradient(90deg,transparent,${C.borderBright} 28%,${C.borderBright} 72%,transparent)` },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 22, flexWrap: 'wrap', margin: '54px 0 20px' },
  sectionTitle: { fontFamily: F.display, fontSize: 40, letterSpacing: 3, lineHeight: 0.95, color: C.text, margin: '10px 0 0', textTransform: 'uppercase' },
  sectionCopy: { maxWidth: 470, color: C.textDim, fontSize: 14, lineHeight: 1.55, margin: 0 },
  bGr: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14 },
  bCd: { minHeight: 170, background: C.card, borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`, padding: '22px 16px', textAlign: 'center', cursor: 'pointer', transition: 'all .22s ease', position: 'relative', overflow: 'hidden' },
  bCdH: { borderLeftColor: C.orange, transform: 'translateY(-4px)', boxShadow: '0 14px 34px rgba(255,107,43,.13)' },
  bTg: { position: 'absolute', top: 0, right: 0, padding: '4px 11px', fontSize: 10, fontWeight: 800, letterSpacing: 1.2, background: `linear-gradient(135deg,${C.red},${C.orange})`, color: 'white' },
  bAm: { fontFamily: F.display, fontSize: 42, lineHeight: 1, color: C.orange, marginBottom: 4 },
  bLb: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, color: C.textDim, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 800 },
  bPrc: { marginTop: 16, fontFamily: F.display, fontSize: 24, color: C.text, letterSpacing: 1 },
  bPer: { marginTop: 4, fontSize: 11, color: C.textVeryDim, letterSpacing: 1 },
  kitGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 },
  kitCard: { background: C.card, borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`, padding: '28px 24px', textAlign: 'center', cursor: 'pointer', transition: 'all .22s ease', position: 'relative' },
  kitCardH: { borderLeftColor: C.orange, transform: 'translateY(-4px)', boxShadow: '0 14px 34px rgba(255,107,43,.13)' },
  kitIcon: { marginBottom: 12 },
  kitName: { fontFamily: F.display, fontSize: 22, letterSpacing: 3, color: C.text, marginBottom: 5 },
  kitDesc: { fontSize: 13, color: C.textDim, letterSpacing: 0.8, marginBottom: 15, lineHeight: 1.45 },
  kitPrice: { fontFamily: F.display, fontSize: 28, color: C.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 },
  kitOwned: { fontSize: 11, color: C.textVeryDim, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 800 },
  pGr: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 },
  pCd: { background: C.card, borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.borderBright}`, padding: '30px 24px', textAlign: 'center', cursor: 'pointer', transition: 'all .22s ease', position: 'relative', overflow: 'hidden' },
  pCdH: { borderLeftColor: C.red, transform: 'translateY(-4px)', boxShadow: '0 14px 34px rgba(232,25,44,.13)' },
  pCdF: { borderLeftColor: C.red },
  pTg: { position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', padding: '4px 14px', fontSize: 10, fontWeight: 800, letterSpacing: 1.2, background: `linear-gradient(135deg,${C.red},${C.orange})`, color: 'white' },
  pLb: { fontFamily: F.display, fontSize: 24, letterSpacing: 3, color: C.textDim, marginBottom: 9 },
  pPr: { fontFamily: F.display, fontSize: 44, lineHeight: 1, color: C.text },
  pPd: { fontSize: 14, color: C.textVeryDim },
  pPerks: { marginTop: 18, textAlign: 'left' },
  pPk: { fontSize: 13, color: C.textDim, padding: '5px 0', display: 'flex', alignItems: 'center', gap: 9 },
  pPkD: { width: 6, height: 6, background: C.red, flexShrink: 0, transform: 'rotate(45deg)' },
  pBtn: { marginTop: 20, width: '100%', minHeight: 42, padding: 11, border: `1.5px solid ${C.red}`, background: 'transparent', color: C.red, cursor: 'pointer', fontFamily: F.display, fontSize: 16, letterSpacing: 2.2, transition: 'all .18s ease' },
  mOv: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.84)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'rp-fi .2s ease', padding: 20 },
  mBx: { position: 'relative', background: C.card, borderTop: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, borderLeft: `2px solid ${C.red}`, padding: 32, textAlign: 'center', maxWidth: 360, width: '100%', animation: 'rp-mi .3s cubic-bezier(.34,1.56,.64,1)' },
  mNm: { fontFamily: F.display, fontSize: 26, margin: '0 0 4px', letterSpacing: 1.5, color: C.text, textTransform: 'uppercase' },
  mTx: { color: C.textDim, fontSize: 14, margin: '8px 0 20px' },
  mBl: { fontSize: 12, color: C.textVeryDim, margin: '-8px 0 16px' },
  mAc: { display: 'flex', gap: 10 },
  mCn: { flex: 1, padding: 11, border: `1.5px solid ${C.borderBright}`, background: 'transparent', color: C.textDim, cursor: 'pointer', fontFamily: F.display, fontSize: 15, letterSpacing: 1.5, transition: 'all .18s ease' },
  mCf: { flex: 1, padding: 11, border: 'none', cursor: 'pointer', fontFamily: F.display, fontSize: 15, letterSpacing: 1.5, background: `linear-gradient(135deg,${C.red},${C.orange})`, color: 'white', transition: 'all .18s ease' },
};